import { useEffect, useRef, useState } from 'react';
import {
  getCachedModelURL,
  onViewerSlotAvailable,
  releaseViewerSlot,
  tryClaimViewerSlot,
} from '@/lib/modelCache';
import { prettyModelName } from '@/lib/utils';

interface Props {
  modelPath: string;
  name?: string;
  size?: 'sm' | 'md';
  /**
   * The scrollable ancestor to observe intersection against. Pass the
   * element that owns `overflow: auto` — otherwise IO uses the browser
   * viewport, and tiles clipped by an ancestor's overflow are still
   * treated as "in view", causing every tile to mount at once.
   */
  scrollRoot?: Element | null;
}

/**
 * Small non-interactive GLB preview using <model-viewer>.
 *
 * Rendering N=69 <model-viewer> elements simultaneously creates N
 * WebGL contexts and holds N parsed glTF scenes in memory — that
 * OOMs the tab. We virtualise: an IntersectionObserver mounts the
 * <model-viewer> only when the tile is on (or near) screen, and
 * unmounts it when it scrolls away. The blob URL stays in the
 * module-level cache so re-mounting is instant with zero network.
 *
 * The wrapping `<div>` keeps its fixed height at all times so the
 * scroll position never jumps.
 */
export function ModelThumbnail({ modelPath, name, size = 'md', scrollRoot }: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [hasSlot, setHasSlot] = useState(false);

  // Observe visibility. `rootMargin` gives us a small pre-load buffer
  // so tiles appear "already loaded" as the user scrolls into them,
  // but not so wide that lots of off-screen tiles stay mounted.
  //
  // If a `scrollRoot` is passed we use it as the IO root so ancestor
  // `overflow: auto` clipping is respected; if it's `null`/undefined
  // (e.g. the parent's ref callback hasn't fired yet) we fall back to
  // the viewport. The global viewer-slot semaphore keeps us safe even
  // if the fallback briefly marks too many tiles as "in view".
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setInView(entry.isIntersecting);
        }
      },
      { root: scrollRoot ?? null, rootMargin: '150px 0px', threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [scrollRoot]);

  // Only fetch/resolve the blob URL when this tile is (nearly) visible.
  useEffect(() => {
    if (!inView) return;
    let cancelled = false;
    getCachedModelURL(modelPath)
      .then((u) => {
        if (!cancelled) setSrc(u);
      })
      .catch(() => {
        /* swallow — placeholder will render */
      });
    return () => {
      cancelled = true;
    };
  }, [inView, modelPath]);

  // Claim a global viewer slot before mounting <model-viewer>. If all
  // slots are taken, wait for one to free up. Always release on unmount
  // or when we scroll out of view.
  useEffect(() => {
    if (!inView || !src) return;
    let claimed = false;
    const attempt = () => {
      if (claimed) return true;
      if (tryClaimViewerSlot()) {
        claimed = true;
        setHasSlot(true);
        return true;
      }
      return false;
    };
    if (attempt()) {
      return () => {
        if (claimed) releaseViewerSlot();
      };
    }
    const unsub = onViewerSlotAvailable(() => {
      if (attempt()) unsub();
    });
    return () => {
      unsub();
      if (claimed) releaseViewerSlot();
    };
  }, [inView, src]);

  // Reset local `hasSlot` state when we scroll out of view so the
  // placeholder renders again. Slot release itself is handled by the
  // effect above via its cleanup.
  useEffect(() => {
    if (!inView || !src) setHasSlot(false);
  }, [inView, src]);

  const height = size === 'sm' ? 90 : 140;

  return (
    <div
      ref={rootRef}
      className="grid w-full place-items-center bg-gradient-to-br from-muted to-background"
      style={{ height }}
      title={name ?? prettyModelName(modelPath)}
    >
      {inView && src && hasSlot ? (
        <model-viewer
          src={src}
          alt={name ?? prettyModelName(modelPath)}
          auto-rotate
          disable-zoom
          interaction-prompt="none"
          reveal="auto"
          loading="eager"
          style={{ width: '100%', height: `${height}px`, background: 'transparent' }}
        />
      ) : (
        <span className="text-[10px] text-muted-foreground">
          {inView ? '…' : ''}
        </span>
      )}
    </div>
  );
}
